import * as React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { api, setAuthToken, setUnauthorizedHandler, invalidateCsrfToken, ApiError } from './api-client';
import { invalidateAll as invalidateApiCache } from './api-cache';
import { useI18n } from './i18n';
import {
  isCognitoEnabled,
  cognitoSignIn,
  cognitoRespondToMfaChallenge,
  cognitoSetupTotp,
  cognitoVerifyTotp,
  cognitoCompleteNewPassword,
  cognitoConfirmPassword,
  cognitoGlobalSignOut,
  type CognitoSignInResult,
  type CognitoAuthTokens,
} from './cognito';
import type { CognitoUser } from 'amazon-cognito-identity-js';

// ─── Types ───────────────────────────────────────────────────────────────────

export type RoleType =
  | 'super_admin'
  | 'administrator'
  | 'supervisor'
  | 'agent'
  | 'collaborator'
  | 'customer';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: RoleType;
  isAgent: boolean;
  /** Effective ticket-action permissions for this user's role (server-computed). */
  permissions?: Record<string, boolean>;
  /**
   * IANA timezone identifier for this user (e.g. "Asia/Kolkata"). Used to
   * render every date/time in the viewer's own configured zone instead of
   * raw browser-local time — see lib/date-format.ts. Sourced fresh from the
   * DB on every session bootstrap/login/refresh, so an admin updating an
   * agent's timezone takes effect on that agent's next request, not only
   * after a full re-login.
   */
  timezone?: string;
  /** User's language preference (e.g. "en"). */
  language?: string;
}

/** Cognito challenge state — held between login steps */
export interface CognitoChallenge {
  type: 'MFA_REQUIRED' | 'MFA_SETUP' | 'NEW_PASSWORD_REQUIRED';
  cognitoUser: CognitoUser;
  totpSecret?: string; // populated during MFA_SETUP after calling setupTotp
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** Active Cognito challenge (null when no challenge pending) */
  challenge: CognitoChallenge | null;
  /** Login with email/password. May set challenge state instead of completing. */
  login: (email: string, password: string) => Promise<void>;
  /** Submit MFA TOTP code to complete login */
  submitMfaCode: (code: string) => Promise<void>;
  /** Setup TOTP — gets secret for QR code */
  setupTotp: () => Promise<string>;
  /** Verify TOTP setup code */
  verifyTotpSetup: (code: string) => Promise<void>;
  /** Complete new password challenge (first login) */
  completeNewPassword: (newPassword: string) => Promise<void>;
  /** Log out and redirect to login */
  logout: () => Promise<void>;
  /** Request password reset email/code */
  forgotPassword: (email: string) => Promise<void>;
  /** Reset password (legacy: token+password, cognito: email+code+password) */
  resetPassword: (tokenOrEmail: string, passwordOrCode: string, newPassword?: string) => Promise<void>;
  /** Check if user has a specific role */
  hasRole: (...roles: RoleType[]) => boolean;
  /** Check if the current user's role grants a ticket-action permission */
  can: (action: string) => boolean;
  /** Clear challenge state (e.g., user navigates away) */
  clearChallenge: () => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [challenge, setChallenge] = React.useState<CognitoChallenge | null>(null);

  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      invalidateCsrfToken();
      setAuthToken(null);
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Session bootstrap — validate cookie on mount
  React.useEffect(() => {
    api
      .get<{ user: AuthUser }>('/auth/me')
      .then((res) => setUser(res.user))
      .catch(() => {
        invalidateCsrfToken();
        setAuthToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // ─── Token refresh timer (Cognito mode) ──────────────────────────────────

  React.useEffect(() => {
    if (!isCognitoEnabled() || !user) return;

    // Refresh 5 minutes before the 1-hour access token expires
    const refreshInterval = (55 * 60) * 1000; // 55 minutes
    const timer = setInterval(async () => {
      try {
        const res = await api.post<{ user: AuthUser }>('/auth/refresh');
        if (res.user) {
          invalidateCsrfToken();
          setUser(res.user);
        }
      } catch {
        // Refresh failed — session expired, will get 401 on next request
      }
    }, refreshInterval);

    return () => clearInterval(timer);
  }, [user]);

  // ─── Login ─────────────────────────────────────────────────────────────────

  const login = React.useCallback(async (email: string, password: string) => {
    if (isCognitoEnabled()) {
      const result = await cognitoSignIn(email, password);
      await handleCognitoResult(result);
    } else {
      const res = await api.post<{ user: AuthUser }>('/auth/login', { email, password });
      setAuthToken(null);
      invalidateCsrfToken();
      setUser(res.user);
    }
  }, []);

  async function handleCognitoResult(result: CognitoSignInResult) {
    switch (result.type) {
      case 'SUCCESS':
        await exchangeTokens(result.tokens);
        break;
      case 'MFA_REQUIRED':
        setChallenge({ type: 'MFA_REQUIRED', cognitoUser: result.cognitoUser });
        break;
      case 'MFA_SETUP':
        setChallenge({ type: 'MFA_SETUP', cognitoUser: result.cognitoUser });
        break;
      case 'NEW_PASSWORD_REQUIRED':
        setChallenge({ type: 'NEW_PASSWORD_REQUIRED', cognitoUser: result.cognitoUser });
        break;
    }
  }

  async function exchangeTokens(tokens: CognitoAuthTokens) {
    const res = await api.post<{ user: AuthUser }>('/auth/cognito-session', tokens);
    setChallenge(null);
    setAuthToken(null);
    setUser(res.user);
  }

  // ─── MFA submission ────────────────────────────────────────────────────────

  const submitMfaCode = React.useCallback(async (code: string) => {
    if (!challenge || challenge.type !== 'MFA_REQUIRED') {
      throw new Error('No MFA challenge active');
    }
    const tokens = await cognitoRespondToMfaChallenge(challenge.cognitoUser, code);
    await exchangeTokens(tokens);
  }, [challenge]);

  // ─── MFA setup ─────────────────────────────────────────────────────────────

  const setupTotp = React.useCallback(async (): Promise<string> => {
    if (!challenge || challenge.type !== 'MFA_SETUP') {
      throw new Error('No MFA setup challenge active');
    }
    const secret = await cognitoSetupTotp(challenge.cognitoUser);
    setChallenge((prev) => prev ? { ...prev, totpSecret: secret } : null);
    return secret;
  }, [challenge]);

  const verifyTotpSetup = React.useCallback(async (code: string) => {
    if (!challenge || challenge.type !== 'MFA_SETUP') {
      throw new Error('No MFA setup challenge active');
    }
    const tokens = await cognitoVerifyTotp(challenge.cognitoUser, code);
    if (tokens) {
      await exchangeTokens(tokens);
    }
  }, [challenge]);

  // ─── New password (first login) ────────────────────────────────────────────

  const completeNewPassword = React.useCallback(async (newPassword: string) => {
    if (!challenge || challenge.type !== 'NEW_PASSWORD_REQUIRED') {
      throw new Error('No new password challenge active');
    }
    const result = await cognitoCompleteNewPassword(challenge.cognitoUser, newPassword);
    await handleCognitoResult(result);
  }, [challenge]);

  // ─── Logout ────────────────────────────────────────────────────────────────

  const logout = React.useCallback(async () => {
    try {
      if (isCognitoEnabled()) {
        await cognitoGlobalSignOut();
      }
      await api.post('/auth/logout');
    } catch {
      // Ignore errors on logout
    }
    invalidateCsrfToken();
    invalidateApiCache();
    setAuthToken(null);
    setUser(null);
    setChallenge(null);
  }, []);

  // ─── Password reset ────────────────────────────────────────────────────────

  const forgotPassword = React.useCallback(async (email: string) => {
    // Always go through the backend: it also handles agents who were invited but have never
    // signed in (Cognito's own forgot-password sends nothing for them) and never reveals
    // whether the address exists.
    await api.post('/auth/forgot-password', { email });
  }, []);

  const resetPassword = React.useCallback(async (tokenOrEmail: string, passwordOrCode: string, newPassword?: string) => {
    if (isCognitoEnabled() && newPassword) {
      // Cognito mode: email, code, newPassword
      await cognitoConfirmPassword(tokenOrEmail, passwordOrCode, newPassword);
    } else {
      // Legacy mode: token, password
      await api.post('/auth/reset-password', { token: tokenOrEmail, password: passwordOrCode });
    }
  }, []);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  const hasRole = React.useCallback(
    (...roles: RoleType[]) => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user],
  );

  const can = React.useCallback(
    (action: string) => {
      // Purely RBAC: the effective permission map is computed server-side from
      // the user's assigned role (getEffectiveTicketActionPermissions), which
      // already falls back to the built-in static matrix for legacy/coarse
      // roles. No role name is special-cased here — super_admin/administrator
      // get their access because the server resolved those actions to true for
      // their role, not because the client hardcodes their role name.
      if (!user) return false;
      return user.permissions?.[action] === true;
    },
    [user],
  );

  const clearChallenge = React.useCallback(() => {
    setChallenge(null);
  }, []);

  const value = React.useMemo(
    () => ({
      user, loading, challenge, login, submitMfaCode, setupTotp,
      verifyTotpSetup, completeNewPassword, logout, forgotPassword,
      resetPassword, hasRole, can, clearChallenge,
    }),
    [user, loading, challenge, login, submitMfaCode, setupTotp,
     verifyTotpSetup, completeNewPassword, logout, forgotPassword,
     resetPassword, hasRole, can, clearChallenge],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ─── Protected Route ─────────────────────────────────────────────────────────

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: RoleType[];
  /** Ticket-action permission key that must be granted (in addition to role). */
  requiredPermission?: string;
}

export function ProtectedRoute({ children, allowedRoles, requiredPermission }: ProtectedRouteProps) {
  const { user, loading, can } = useAuth();
  const { t } = useI18n();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" aria-live="polite">
        <div className="text-sm text-ink-subtle">{t('common.loading')}</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-danger-600">{t('common.accessDenied')}</h1>
        <p className="mt-2 text-sm text-ink-muted">
          {t('common.noPagePermission')}
        </p>
      </div>
    );
  }

  if (requiredPermission && !can(requiredPermission)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-danger-600">{t('common.accessDenied')}</h1>
        <p className="mt-2 text-sm text-ink-muted">
          {t('common.roleNoPermission')}
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

// Re-export ApiError for convenience
export { ApiError };
